import { plural, t } from '@lingui/core/macro';
import { CalendarEntry, MonthCalendar, MonthCalendarDayInfo, MonthCalendarProps } from 'noddi-ui-web';
import { CarProps, formatCarLicensePlate, getCarDisplayName } from 'noddi-util';
import { ReactNode } from 'react';
import {
  CALENDAR_ENTRY_STATUS,
  FleetCalendarBooking,
  formatTimeOfDay,
  getCalendarStatusKey
} from '../../utils/fleetCalendarBookingDisplay';
import { BookingCalendarLegend } from './BookingCalendarLegend';

interface Props {
  currentDate: Date;
  onMonthChange: (date: Date) => void;
  onDayClick: (info: MonthCalendarDayInfo) => void;
  bookingsByDate: Map<string, FleetCalendarBooking[]>;
  cellSize?: MonthCalendarProps['cellSize'];
  headerActions?: ReactNode;
  loading?: boolean;
}

const MAX_PILLS_PER_DAY = 3;

/** The month grid shared by the home page card and the full fleet calendar. */
export const BookingCalendarMonth = ({
  currentDate,
  onMonthChange,
  onDayClick,
  bookingsByDate,
  cellSize,
  headerActions,
  loading
}: Props) => {
  return (
    <div className='flex flex-col gap-4'>
      <BookingCalendarLegend />

      <MonthCalendar
        currentDate={currentDate}
        onMonthChange={onMonthChange}
        onDayClick={onDayClick}
        todayLabel={t`Today`}
        headerActions={headerActions}
        cellSize={cellSize}
        loading={loading}
        renderDayContent={(info) => {
          const dayBookings = bookingsByDate.get(info.dateString) ?? [];
          if (dayBookings.length === 0) {
            return null;
          }

          const handlePillClick = (event: { stopPropagation: () => void }) => {
            event.stopPropagation();
            onDayClick(info);
          };

          const visibleBookings = dayBookings.slice(0, MAX_PILLS_PER_DAY);
          const overflow = dayBookings.length - visibleBookings.length;
          const carCount = dayBookings.reduce((sum, booking) => sum + booking.bookingItems.length, 0);

          return (
            <div className='flex h-full min-h-0 flex-col gap-1'>
              <div className='flex min-h-0 flex-1 flex-col gap-0.5 overflow-hidden'>
                {visibleBookings.map((booking) => {
                  const status = getCalendarStatusKey(booking.status.value);
                  if (!status) {
                    return null;
                  }
                  return (
                    <CalendarEntry
                      key={booking.id}
                      status={CALENDAR_ENTRY_STATUS[status]}
                      time={formatTimeOfDay(booking.deliveryWindowStartsAt)}
                      label={carsLabel(booking.bookingItems)}
                      onClick={handlePillClick}
                    />
                  );
                })}
                {overflow > 0 && (
                  <span className='px-1 text-[11px] leading-tight font-medium text-muted-foreground'>{t`+${overflow} more`}</span>
                )}
              </div>
              <span className='self-end text-[11px] font-medium text-muted-foreground'>
                {plural(carCount, { one: '# car', other: '# cars' })}
              </span>
            </div>
          );
        }}
      />
    </div>
  );
};

type CarItem = { car: CarProps };

/** The plate is the car's identity in a fleet and survives the truncation a cell forces. */
const carsLabel = (items: CarItem[]) =>
  items
    .map(
      (item) =>
        formatCarLicensePlate(item.car) ||
        getCarDisplayName({ car: item.car, showLicensePlate: false, showYear: false })
    )
    .join(' · ');
